// campaignEditor.js — campaign composer (page-level)
//
// State is plain useState (not Formik) because the nested structure with channel-specific
// payloads + recursive segment ruleTree doesn't benefit from Formik's flat-field model.
// Validation is a small `validateCampaign` helper.
//
// See MARKETING_CAMPAIGNS_PLAN.md §3.3 (Campaign data model), §5 (composer phase).

import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button, Card, CardBody, Col, FormGroup, Input, Label, Row,
} from 'reactstrap';
import { firestore, functions } from '../../../../helpers/Firebase';
import { validateRuleTree } from '../segments/helpers/segmentResolver.js';
import { actorFromUser, computeDiff, writeAuditEntry } from '../shared/auditLog.js';
import DynamicFields from '../shared/DynamicFields.jsx';
import CampaignReachPanel from './components/CampaignReachPanel.jsx';
import ChannelPushForm from './components/ChannelPushForm.jsx';
import IssuesPanel from './components/IssuesPanel.jsx';
import ScheduleForm from './components/ScheduleForm.jsx';
import SegmentPicker from './components/SegmentPicker.jsx';
import { detectOverlaps } from './helpers/overlapCheck.js';
import { getRoute } from './helpers/routeCatalog.js';

const EMPTY_TREE = () => ({ match: 'all', rules: [{ field: null, op: null, value: undefined }] });

const emptyCampaign = () => ({
  name: '',
  description: '',
  // Push-only. In-app messaging was removed from the composer since marketing
  // is focused on push. `channels` kept for forward-compat with the campaign
  // schema; the dispatcher (dispatchCampaignPending) reads `payload.push` only.
  channels: ['push'],
  payload: {
    push: { localized: { en: {}, ar: {} }, deepLink: { route: 'home', params: {} } },
  },
  audience: { segmentId: null, segmentName: null, ruleTree: EMPTY_TREE() },
  schedule: {
    type: 'sendAt',
    sendAt: Date.now() + 60 * 60 * 1000,
    timezone: 'Asia/Dubai',
    quietHours: { startHour: 22, endHour: 8 },
    frequencyCapHours: 24,
  },
  testRecipients: [],
});

// Pure validator — returns array of error strings (empty = valid).
// Kept for canSaveDraft / canSchedule gating; the sidebar checklist uses
// getCampaignChecks below for richer per-item severity.
const validateCampaign = (c, { strict = false } = {}) => {
  const errors = [];
  if (!c.name || !c.name.trim()) errors.push('Name is required');

  const en = c.payload?.push?.localized?.en || {};
  const ar = c.payload?.push?.localized?.ar || {};
  if (!en.title || !en.body) errors.push('EN title and body are required');
  if (strict && (!ar.title || !ar.body)) errors.push('AR title and body are required for launch');

  const dl = c.payload?.push?.deepLink;
  const routeDef = dl?.route && getRoute(dl.route);
  if (routeDef) {
    for (const p of routeDef.params || []) {
      if (p.autoFill) continue;
      if (p.required && !dl.params?.[p.key]) {
        errors.push(`Tap destination requires "${p.label}"`);
      }
    }
  }

  const treeErrors = validateRuleTree(c.audience?.ruleTree || EMPTY_TREE());
  if (treeErrors.length) errors.push(...treeErrors.map(e => `Audience: ${e}`));

  if (strict && c.schedule?.type === 'sendAt' && c.schedule.sendAt < Date.now() - 60000) {
    errors.push('Scheduled send time is in the past');
  }
  return errors;
};

// Build the per-item checklist for the sidebar IssuesPanel.
// Each entry: { id, label, status: 'pass'|'warning'|'error', detail? }.
// Errors block Schedule; warnings are advisory (Arabic missing = warning).
const getCampaignChecks = (c) => {
  const checks = [];

  // Name
  checks.push({
    id: 'name',
    label: c.name?.trim() ? 'Name is set' : 'Add a campaign name',
    status: c.name?.trim() ? 'pass' : 'error',
  });

  // English content
  const en = c.payload?.push?.localized?.en || {};
  const enOk = !!(en.title && en.body);
  checks.push({
    id: 'content-en',
    label: enOk ? 'English content complete' : 'English title and body are required',
    status: enOk ? 'pass' : 'error',
  });

  // Arabic content (advisory — English will be shown if AR missing)
  const ar = c.payload?.push?.localized?.ar || {};
  const arOk = !!(ar.title && ar.body);
  checks.push({
    id: 'content-ar',
    label: arOk ? 'Arabic content complete' : 'Add Arabic translation',
    detail: arOk ? null : 'Arabic-speaking customers will fall back to English.',
    status: arOk ? 'pass' : 'warning',
  });

  // Tap destination — required params on the chosen deep-link route
  const dl = c.payload?.push?.deepLink;
  const routeDef = dl?.route && getRoute(dl.route);
  const missingDlParam = routeDef && (routeDef.params || []).find(p => !p.autoFill && p.required && !dl.params?.[p.key]);
  checks.push({
    id: 'destination',
    label: missingDlParam ? `Tap destination needs "${missingDlParam.label}"` : 'Tap destination is set',
    status: missingDlParam ? 'error' : 'pass',
  });

  // Audience
  const tree = c.audience?.ruleTree || EMPTY_TREE();
  const treeErrors = validateRuleTree(tree);
  const isEmptyAudience = Array.isArray(tree.rules) && tree.rules.length === 0;
  let audienceLabel;
  let audienceDetail = null;
  if (treeErrors.length) {
    audienceLabel = 'Audience rules need fixing';
    audienceDetail = treeErrors[0];
  } else if (isEmptyAudience) {
    audienceLabel = 'No filters — will target every reachable user';
    audienceDetail = 'This campaign has no audience rules and will be sent to the entire user base.';
  } else if (c.audience?.segmentName) {
    audienceLabel = `Audience: ${c.audience.segmentName}`;
  } else {
    audienceLabel = 'Audience rules look valid';
  }
  checks.push({
    id: 'audience',
    label: audienceLabel,
    detail: audienceDetail,
    status: treeErrors.length ? 'error' : (isEmptyAudience ? 'warning' : 'pass'),
  });

  // Schedule — only relevant when sendAt (sendNow always OK)
  if (c.schedule?.type === 'sendAt') {
    const inPast = c.schedule.sendAt < Date.now() - 60000;
    checks.push({
      id: 'schedule',
      label: inPast ? 'Send time is in the past' : 'Send time is scheduled',
      detail: inPast ? 'Pick a time in the future.' : null,
      status: inPast ? 'error' : 'pass',
    });
  } else {
    checks.push({
      id: 'schedule',
      label: 'Will send immediately on launch',
      status: 'pass',
    });
  }

  return checks;
};

const CampaignEditor = ({ match, history }) => {
  const campaignId = match.params.id || null;
  const isNew = !campaignId;
  const currentUser = useSelector(s => s.authUser?.user);

  const [campaign, setCampaign] = useState(emptyCampaign());
  const [originalCampaign, setOriginalCampaign] = useState(null); // baseline for audit diff
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [testRecipientsInput, setTestRecipientsInput] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [activeAutomations, setActiveAutomations] = useState([]);
  const [overlapDismissed, setOverlapDismissed] = useState(false);

  // Live-listen to active automations so the overlap warning is always current
  useEffect(() => {
    const unsub = firestore()
      .collection('marketing_automations')
      .where('active', '==', true)
      .onSnapshot(
        (snap) => setActiveAutomations(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
        (e) => console.error('active automations listener', e)
      );
    return () => unsub();
  }, []);

  // Recompute overlaps whenever the campaign's audience or the active-automation
  // set changes. Stringify the ruleTree so React sees identity changes.
  const overlaps = useMemo(
    () => detectOverlaps(campaign.audience?.ruleTree, activeAutomations),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(campaign.audience?.ruleTree), activeAutomations]
  );

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await firestore().collection('marketing_campaigns').doc(campaignId).get();
        if (cancelled) return;
        if (!snap.exists) {
          setError('Campaign not found');
        } else {
          const data = snap.data();
          const loaded = { ...emptyCampaign(), ...data };
          setCampaign(loaded);
          setOriginalCampaign(loaded);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId, isNew]);

  const update = (patch) => setCampaign({ ...campaign, ...patch });

  // const draftErrors = validateCampaign(campaign, { strict: false });
  const scheduleErrors = validateCampaign(campaign, { strict: true });
  const canSaveDraft = campaign.name?.trim() && !saving;
  const canSchedule = scheduleErrors.length === 0 && !saving;
  const checks = getCampaignChecks(campaign);

  // Once a campaign flips past 'scheduled', it's either mid-send or done —
  // editing serves no purpose and can only cause confusion (the dispatcher
  // has already read the doc into memory or won't re-read it). Marketers
  // can clone via 'Duplicate as draft' instead.
  const LOCKED_STATUSES = ['running', 'completed', 'canceled'];
  const status = campaign.status || 'draft';
  const isLocked = LOCKED_STATUSES.includes(status);
  const isScheduled = status === 'scheduled';

  const duplicateAsDraft = async () => {
    setSaving(true);
    setError(null);
    try {
      const cloned = {
        ...campaign,
        name: `${campaign.name || 'Untitled'} (copy)`,
        status: 'draft',
        createdAt: Date.now(),
        createdBy: currentUser?.uid || null,
        createdByName: currentUser?.displayName || currentUser?.email || null,
        updatedAt: Date.now(),
        updatedBy: currentUser?.uid || null,
        updatedByName: currentUser?.displayName || currentUser?.email || null,
      };
      // Strip run-time fields — new draft starts clean
      delete cloned.launchedAt;
      delete cloned.completedAt;
      delete cloned.segmentSnapshot;
      delete cloned.stats;
      const ref = await firestore().collection('marketing_campaigns').add(cloned);
      history.push(`/app/marketing/campaigns/${ref.id}/edit`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const persist = async (statusOverride) => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...campaign,
        status: statusOverride || campaign.status || 'draft',
        updatedAt: Date.now(),
        updatedBy: currentUser?.uid || null,
        updatedByName: currentUser?.displayName || currentUser?.email || null,
      };
      if (statusOverride === 'scheduled') {
        // Snapshot the segment's ruleTree at schedule time (per plan §3.3)
        payload.segmentSnapshot = campaign.audience?.ruleTree;
        payload.launchedAt = Date.now();
        // Denormalize schedule.sendAt to top-level `sendAt` so the
        // scheduleCampaignTick cron (queries `where('sendAt','<=',now)`)
        // picks it up. Without this the tick matched no docs and
        // campaigns stayed on 'scheduled' past their sendAt time.
        payload.sendAt = campaign.schedule?.sendAt || Date.now();
      } else if (statusOverride === 'draft') {
        // Removing from queue — clear top-level sendAt so if this doc is
        // ever inspected by the tick it won't match anything.
        payload.sendAt = null;
      }
      let savedId = campaignId;
      if (isNew) {
        payload.createdAt = Date.now();
        payload.createdBy = currentUser?.uid || null;
        payload.createdByName = currentUser?.displayName || currentUser?.email || null;
        const ref = await firestore().collection('marketing_campaigns').add(payload);
        savedId = ref.id;
        history.replace(`/app/marketing/campaigns/${ref.id}/edit`);
      } else {
        await firestore().collection('marketing_campaigns').doc(campaignId).set(payload, { merge: true });
      }

      // Audit log — fire-and-forget
      const actor = actorFromUser(currentUser);
      if (isNew) {
        writeAuditEntry('marketing_campaigns', savedId, { ...actor, action: 'created' });
      } else if (originalCampaign) {
        const changes = computeDiff(originalCampaign, payload);
        // Emit status-transition entries alongside the edit
        if ('status' in changes) {
          const to = changes.status.to;
          const map = { scheduled: 'scheduled', running: 'launched', canceled: 'canceled', completed: 'completed' };
          if (map[to]) writeAuditEntry('marketing_campaigns', savedId, { ...actor, action: map[to] });
        }
        if (Object.keys(changes).length > 0) {
          writeAuditEntry('marketing_campaigns', savedId, { ...actor, action: 'edited', changes });
        }
        setOriginalCampaign(payload);
      } else if (statusOverride) {
        // First save was actually a status change (e.g. scheduling a draft-in-memory)
        const map = { scheduled: 'scheduled', running: 'launched', canceled: 'canceled', completed: 'completed' };
        if (map[statusOverride]) writeAuditEntry('marketing_campaigns', savedId, { ...actor, action: map[statusOverride] });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading" />;

  return (
    <>
      {/* Page header — title left, action buttons right (ListPageHeading pattern) */}
      <Row className="mb-3">
        <Col xs="12" md="6">
          <h1 className="mb-0 p-0">{isNew ? 'New campaign' : 'Edit campaign'}</h1>
        </Col>
        <Col xs="12" md="6">
          <div className="d-flex align-items-center justify-content-end" style={{ gap: 8 }}>
            <Button
              color="danger" outline size="lg"
              onClick={() => history.push('/app/marketing/campaigns')}
            >
              {isLocked ? 'Back' : 'Dismiss'}
            </Button>
            {isLocked ? (
              <Button
                color="primary" size="lg"
                disabled={saving}
                onClick={duplicateAsDraft}
              >
                {saving ? 'Cloning…' : 'Duplicate as draft'}
              </Button>
            ) : (
              <>
                <Button
                  color="light" size="lg"
                  disabled={!canSaveDraft}
                  onClick={() => persist('draft')}
                >
                  {saving ? 'Saving…' : 'Save draft'}
                </Button>
                <Button
                  color="primary" size="lg"
                  disabled={!canSchedule}
                  onClick={() => persist('scheduled')}
                >
                  {saving ? 'Scheduling…' : (campaign.schedule.type === 'sendNow' ? 'Send now' : 'Schedule')}
                </Button>
              </>
            )}
          </div>
          {error && <div className="text-danger small text-right mt-2">Error: {error}</div>}
        </Col>
      </Row>

      <Row>
        <Col xs="12" md="8">
          {isLocked && (
            <Card className="mb-3" style={{ borderLeft: '4px solid #6c757d', background: '#f8f9fa' }}>
              <CardBody className="py-3">
                <div style={{ fontWeight: 600, color: '#495057' }}>
                  {status === 'running' && 'This campaign is sending right now.'}
                  {status === 'completed' && 'This campaign has already been sent.'}
                  {status === 'canceled' && 'This campaign was canceled.'}
                </div>
                <div className="text-muted small mt-1">
                  It's locked to preserve the send record. Use "Duplicate as draft" to
                  reuse the copy or audience for a new campaign.
                </div>
              </CardBody>
            </Card>
          )}

          {isScheduled && (
            <Card className="mb-3" style={{ borderLeft: '4px solid #4c8bf5', background: '#eef4ff' }}>
              <CardBody className="py-3">
                <div style={{ fontWeight: 600, color: '#2b62d1' }}>
                  Scheduled to send at {new Date(campaign.schedule?.sendAt || 0).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', dateStyle: 'medium', timeStyle: 'short' })} (Asia/Dubai).
                </div>
                <div className="text-muted small mt-1">
                  Any edits you Schedule again will be included in the upcoming send.
                  "Save draft" will remove it from the queue.
                </div>
              </CardBody>
            </Card>
          )}

          <div style={isLocked ? { pointerEvents: 'none', opacity: 0.65 } : null}>
            <Card className="mb-3">
              <CardBody>
                <DynamicFields
                  value={campaign}
                  onChange={setCampaign}
                  elements={[
                    { key: 'name', title: 'Name', type: 'text', width: '12', required: true, maxLength: 100, placeholder: 'e.g. Ramadan kickoff — Dubai high-value' },
                    { key: 'description', title: 'Description (internal)', type: 'textarea', width: '12', rows: 2, maxLength: 500, placeholder: 'What this campaign is for' },
                  ]}
                />
              </CardBody>
            </Card>

            {/* Notification — single channel for v1. `channels: ['push']` stays on
            the campaign doc for the dispatcher's channel check but is not
            surfaced in the UI. */}
            <Card className="mb-3">
              <CardBody>
                <h6 className="mb-3">Notification</h6>
                <ChannelPushForm
                  value={campaign.payload.push}
                  onChange={(v) => update({ payload: { ...campaign.payload, push: v } })}
                  context="campaign"
                />
              </CardBody>
            </Card>

            {/* Audience */}
            <Card className="mb-3">
              <CardBody>
                <h6 className="mb-3">Audience</h6>
                <SegmentPicker
                  value={campaign.audience}
                  onChange={(v) => update({ audience: v })}
                />

                {overlaps.length > 0 && !overlapDismissed && (
                  <div
                    className="mt-3 p-3 rounded"
                    style={{ background: '#fff8e1', border: '1px solid #f0ad4e' }}
                  >
                    <div className="d-flex align-items-start">
                      <div style={{ fontSize: 20, marginRight: 12, lineHeight: 1 }}>⚠️</div>
                      <div style={{ flex: 1 }}>
                        <strong>Overlaps with active automations</strong>
                        <div className="small mt-1 mb-2">
                          Recipients may receive two pushes if this campaign fires close in time
                          to one of these automations. Consider narrowing the segment or delaying
                          the send.
                        </div>
                        <ul className="mb-0 small" style={{ paddingLeft: 18 }}>
                          {overlaps.map(({ automation, reasons }) => (
                            <li key={automation.id}>
                              <strong>{automation.name || automation.id}</strong>
                              {' — '}
                              <span className="text-muted">{reasons.join('; ')}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <Button
                        close
                        aria-label="Dismiss"
                        onClick={() => setOverlapDismissed(true)}
                        style={{ marginLeft: 8, marginTop: -4 }}
                      />
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Schedule */}
            <Card className="mb-3">
              <CardBody>
                <h6 className="mb-3">Schedule & frequency</h6>
                <ScheduleForm
                  value={campaign.schedule}
                  onChange={(v) => update({ schedule: v })}
                />
              </CardBody>
            </Card>
          </div>
        </Col>

        <Col xs="12" md="4">
          {!isLocked && (
            <CampaignReachPanel
              ruleTree={campaign.audience?.ruleTree}
              schedule={campaign.schedule}
            />
          )}

          {/* Test send — hidden when the campaign is locked (running/completed/canceled).
            Sending test pushes for a campaign that already dispatched is confusing.
            Visual parity otherwise: same <small> description tag, small-size
            FormGroup+Label+Input, solid primary CTA, success/danger result line. */}
          {!isLocked && (
            <Card className="mb-3 mt-3">
              <CardBody>
                <h6 className="mb-2">Test send</h6>
                <small className="text-muted d-block mb-2">
                  Push the current draft to specific recipients without writing analytics or affecting stats.
                  Phone numbers (with country code) or user UIDs, comma- or newline-separated. Max 20.
                </small>
                <FormGroup>
                  <Label size="sm">Recipients</Label>
                  <Input
                    bsSize="sm"
                    type="textarea" rows={2}
                    value={testRecipientsInput}
                    onChange={(e) => setTestRecipientsInput(e.target.value)}
                    placeholder="+971111111118, anotherUid, +9715xxx…"
                  />
                </FormGroup>
                <Button
                  color="primary" size="sm"
                  disabled={testSending || !testRecipientsInput.trim()}
                  onClick={async () => {
                    setTestSending(true); setTestResult(null); setError(null);
                    try {
                      const recipients = testRecipientsInput.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
                      const callable = functions().httpsCallable('testSendCampaign');
                      const r = await callable({ campaign, recipients });
                      setTestResult(r.data);
                    } catch (e) {
                      setError(e.message);
                    } finally {
                      setTestSending(false);
                    }
                  }}
                >
                  {testSending ? 'Sending…' : 'Send test push'}
                </Button>
                {testResult && (
                  <div className={`mt-2 small ${testResult.sent > 0 ? 'text-success' : 'text-danger'}`}>
                    {testResult.sent} sent · {testResult.failed} failed · {testResult.skipped} skipped
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {!isLocked && <IssuesPanel checks={checks} />}
        </Col>
      </Row>
    </>
  );
};

export default CampaignEditor;
